import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, tap, map } from 'rxjs/operators';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { CampaignDto } from './../campaign/campaign.dto';
import { BindingDto } from './../entity/binding.dto';
import { CampaignListResponseDto } from './../campaign/campaign-list-response.dto';
import { PageQueriesModel } from './../shared/page-queries.model';
import { PageDto } from './../shared/page.dto';

@Injectable()
export class CampaignRestService {
  campaignId: string;
  dataChange: BehaviorSubject<CampaignDto[]> = new BehaviorSubject<
    CampaignDto[]
  >([]);
  get data(): CampaignDto[] {
    return this.dataChange.value;
  }

  searchChange: BehaviorSubject<string> = new BehaviorSubject<string>('');
  get searchValue(): string {
    return this.searchChange.value;
  }

  bindingChange: BehaviorSubject<BindingDto[]> = new BehaviorSubject<
    BindingDto[]
  >([]);
  get bindingData(): BindingDto[] {
    return this.bindingChange.value;
  }

  private campaignManagementUrl = 'api/campaignManagement';

  private campaignUrl = 'api/campaignManagement/clients/accounts/campaigns'; // URL to web api

  constructor(private http: HttpClient) {}

  list(
    queries: PageQueriesModel,
    clientId: string = null,
    accountId: string = null
  ) {
    const url = this.getCampaignsUrl(clientId, accountId);
    console.log(url);

    const options = {
      params: queries.getParams()
    };
    console.log(options);

    return this.http.get<PageDto<CampaignDto>>(url, options).pipe(
      map(d => {
        const page = new PageDto(d);
        page.content = d.content.map(i => new CampaignDto(i));
        return page;
      })
    );
  }

  getCampaigns(
    page: number,
    pageSize: number
  ): Observable<CampaignListResponseDto> {
    const url = `${this.campaignManagementUrl}/clients/accounts/campaigns/`;
    return this.http
      .get<CampaignListResponseDto>(
        `${url}?page=${page}&pageSize=${pageSize}&options=f`
      )
      .pipe(
        catchError(this.handleError<CampaignListResponseDto>('getCampaigns'))
      );
  }

  getCampaignsForClientId(queries: PageQueriesModel, clientId: string) {
    const options = {
      params: queries.getParams(),
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        clientId: clientId
      })
    };

    const url = `${
      this.campaignManagementUrl
    }/clients/${clientId}/accounts/campaigns`;
    return this.http.get<PageDto<CampaignDto>>(url, options).pipe(
      map(d => {
        const page = new PageDto(d);
        page.content = d.content.map(i => new CampaignDto(i));
        return page;
      })
    );
  }

  getCampaign(
    clientId: string,
    accountId: string,
    id: string
  ): Observable<CampaignDto> {
    const url = `${
      this.campaignManagementUrl
    }/clients/${clientId}/accounts/${accountId}/campaigns/${id}`;
    return this.http.get<CampaignDto>(url).pipe(
      tap(_ => this.log(`fetched campaign id=${id}`)),
      catchError(this.handleError<CampaignDto>(`getCampaign id=${id}`))
    );
  }

  get(id: string): Observable<CampaignDto> {
    const url = `${this.campaignManagementUrl}/campaigns/${id}`;
    return this.http.get<CampaignDto>(url).pipe(
      tap(_ => this.log(`fetched campaign id=${id}`)),
      catchError(this.handleError<CampaignDto>(`getCampaign id=${id}`))
    );
  }

  save(
    clientId: string,
    accountId: string,
    campaign: CampaignDto
  ): Observable<CampaignDto> {
    const url = `${
      this.campaignManagementUrl
    }/clients/${clientId}/accounts/${accountId}/campaigns`;
    console.log(url);
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };
    console.log(campaign);
    return this.http.post(url, campaign, options).pipe(
      tap((createdCampaign: CampaignDto) => {
        if (createdCampaign.errors) {
          this.log(`Creating campaign failed w/ errors`);
        } else {
          this.log(`Creating campaign w/ id=${createdCampaign.identity.id}`);
        }
      }),
      catchError(this.handleError<CampaignDto>('save'))
    );
  }

  update(clientId: string, campaign: CampaignDto): Observable<CampaignDto> {
    const url = `${this.campaignManagementUrl}/clients/${clientId}/accounts/${
      campaign.entity.parent.id
    }/campaigns/${campaign.identity.id}`;
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        clientId: campaign.entity.parent.id
      })
    };
    return this.http.put(url, campaign, options).pipe(
      tap((acc: CampaignDto) => {
        if (acc.errors) {
          this.log(`updated campaign failed with errors`);
        } else {
          this.log(`updated campaign id=${acc.identity.id}`);
        }
      }),
      catchError(this.handleError<CampaignDto>('update'))
    );
  }

  patch(clientId: string, campaign: CampaignDto): Observable<CampaignDto> {
    const url = `${this.campaignManagementUrl}/clients/${clientId}/accounts/${
      campaign.entity.parent.id
    }/campaigns/${campaign.identity.id}`;
    console.log(url);

    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        clientId: clientId
      })
    };
    return this.http.patch(url, campaign, options).pipe(
      tap((acc: CampaignDto) => {
        if (acc.errors) {
          this.log(`patched campaign failed with errors`);
        } else {
          this.log(`patched campaign id=${acc.identity.id}`);
          this.bindingChange.next(acc.entity.bindings);
        }
      }),
      catchError(this.handleError<CampaignDto>('patch'))
    );
  }

  search(searchString: string): void {
    this.searchChange.next(searchString);
  }

  put(campaign: CampaignDto): Observable<CampaignDto> {
    const url =
      `${this.campaignManagementUrl}/clients/${campaign.clientId}/accounts/` +
      `${campaign.entity.parent.id}/campaigns/${campaign.identity.id}`;
    console.log(url);

    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        accountId: campaign.entity.parent.id
      })
    };

    return this.http.post(url, campaign, options).pipe(
      tap((campaign1: CampaignDto) => {
        if (campaign1.errors) {
          this.log(`adding campaign failed with errors`);
        } else {
          this.log(`added campaign w/ id=${campaign1.identity.id}`);
        }
      }),
      map(i => new CampaignDto(i)),
      catchError(this.handleError<CampaignDto>('save'))
    );
  }

  getBindingsByClientId(id: string): Observable<CampaignDto> {
    const url = `${this.campaignUrl}/${id}`;
    return this.http.get<CampaignDto>(url).pipe(
      tap(_ => this.log(`fetched productline id=${id}`)),
      catchError(
        this.handleError<CampaignDto>(`getBindingsByClientId id=${id}`)
      )
    );
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  private log(message: string) {
    console.log('Campaign Service: ' + message);
  }

  private getCampaignsUrl(
    clientId: string | undefined,
    accountId: string | undefined
  ): string {
    // console.log("getCampaignsUrl clientId : " + clientId);
    // console.log("getCampaignsUrl accountId : " + accountId);

    if (clientId) {
      if (accountId) {
        return `${
          this.campaignManagementUrl
        }/clients/${clientId}/accounts/${accountId}/campaigns`;
      } else {
        return `${
          this.campaignManagementUrl
        }/clients/${clientId}/accounts/campaigns`;
      }
    } else {
      return this.campaignUrl;
    }
  }
}
